import { useEffect, useRef, useState } from "react";
import { listClients, createClient, updateClient, deleteClient, aiAnalyzeContractPdf } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Plus, MapPin, Phone, Mail, Trash2, Edit3, Upload, Loader2, Download, FileDown, FileCode, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { exportClientsCsvUrl, clientsTemplateCsvUrl, importClientsCsv, importFatturaPaXml } from "../lib/api";

const empty = { codice_cliente: "", name: "", address: "", city: "", cap: "", provincia: "",
  sede_legale_address: "", sede_legale_city: "", sede_legale_cap: "", sede_legale_provincia: "",
  contact_name: "", phone: "", email: "", piva: "", codice_fiscale: "", codice_destinatario: "", pec: "", notes: "",
  coords_text: "" };

// Parse strings like "45.4642, 9.1900" · "45.4642 9.1900" · "45.4642;9.1900" · "45,4642 9,1900"
function parseCoords(str) {
  if (!str || !str.trim()) return { ok: false, lat: null, lng: null };
  const s = str.trim().replace(/[°Nn]/gi, "").replace(/[EWSewsn]/gi, " ");
  // Try "lat, lng" using . as decimal
  let m = s.match(/^\s*(-?\d+(?:\.\d+)?)[,;\s]+(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) {
    // Handle Italian style with comma as decimal separator: "45,4642 9,1900"
    const t = s.replace(/(\d),(\d)/g, "$1.$2");
    m = t.match(/^\s*(-?\d+(?:\.\d+)?)[,;\s]+(-?\d+(?:\.\d+)?)\s*$/);
  }
  if (!m) return { ok: false, lat: null, lng: null };
  const lat = Number(m[1]), lng = Number(m[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return { ok: false, lat: null, lng: null };
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return { ok: false, lat: null, lng: null };
  return { ok: true, lat, lng };
}

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const csvRef = useRef(null);
  const xmlRef = useRef(null);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const sortedItems = [...items].sort((a, b) => {
    const av = (a[sortKey] ?? "").toString().toLowerCase();
    const bv = (b[sortKey] ?? "").toString().toLowerCase();
    const cmp = av.localeCompare(bv, "it", { numeric: true, sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const load = () => listClients().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleImportXml = async (files) => {
    if (!files || files.length === 0) return;
    setImporting(true);
    try {
      const r = await importFatturaPaXml(files);
      toast.success(`${r.imported} nuovi, ${r.updated} aggiornati, ${r.skipped} saltati (${r.files_total} file)`);
      if (r.errors?.length) r.errors.slice(0, 3).forEach((m) => toast.error(m));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import XML fallito"); }
    finally { setImporting(false); if (xmlRef.current) xmlRef.current.value = ""; }
  };

  const handleImportCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await importClientsCsv(file);
      toast.success(`${r.imported} nuovi, ${r.updated} aggiornati${r.errors?.length ? " · alcuni errori" : ""}`);
      if (r.errors?.length) console.warn("CSV import errors:", r.errors);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import CSV fallito"); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const handleImportPdf = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF troppo grande (max 15MB)"); return; }
    setImporting(true);
    try {
      const r = await aiAnalyzeContractPdf(file);
      const d = r.extracted || {};
      setEditId(null);
      setForm({
        codice_cliente: "",
        name: d.client_name || "",
        address: d.address || "",
        city: d.city || "",
        cap: "", provincia: "",
        contact_name: d.contact_name || "",
        phone: d.phone || "",
        email: d.email || "",
        piva: "", codice_fiscale: "",
        codice_destinatario: "", pec: "",
        notes: d.notes || "",
      });
      setOpen(true);
      toast.success(`PDF letto (${r.pages || "?"} pagine) - controlla i dati e salva`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Estrazione PDF fallita");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.name) { toast.error("Ragione sociale obbligatoria"); return; }
    // Extract GPS coords if pasted by user (bypasses geocoding)
    const parsed = parseCoords(form.coords_text);
    if (form.coords_text && !parsed.ok) {
      toast.error("Coordinate non valide. Formato: 45.4642, 9.1900");
      return;
    }
    const { coords_text: _ct, ...rest } = form;
    const payload = parsed.ok
      ? { ...rest, lat: parsed.lat, lng: parsed.lng }
      : { ...rest, lat: null, lng: null };
    try {
      if (editId) {
        await updateClient(editId, payload);
        toast.success("Cliente aggiornato");
      } else {
        await createClient(payload);
        toast.success("Cliente aggiunto");
      }
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (_e) { toast.error("Errore salvataggio"); }
  };

  const edit = (c) => {
    setEditId(c.id);
    setForm({
      codice_cliente: c.codice_cliente || "",
      name: c.name, address: c.address, city: c.city || "", cap: c.cap || "", provincia: c.provincia || "",
      sede_legale_address: c.sede_legale_address || "",
      sede_legale_city: c.sede_legale_city || "",
      sede_legale_cap: c.sede_legale_cap || "",
      sede_legale_provincia: c.sede_legale_provincia || "",
      contact_name: c.contact_name || "", phone: c.phone || "", email: c.email || "",
      piva: c.piva || "", codice_fiscale: c.codice_fiscale || "",
      codice_destinatario: c.codice_destinatario || "", pec: c.pec || "",
      notes: c.notes || "",
      coords_text: (c.lat != null && c.lng != null) ? `${c.lat}, ${c.lng}` : "",
    });
    setOpen(true);
  };

  const copyFromSedeLegale = () => {
    setForm((f) => ({
      ...f,
      address: f.sede_legale_address,
      city: f.sede_legale_city,
      cap: f.sede_legale_cap,
      provincia: f.sede_legale_provincia,
    }));
    toast.success("Sede operativa copiata da sede legale");
  };

  const remove = async (id) => {
    if (!confirm("Eliminare cliente?")) return;
    await deleteClient(id);
    toast.success("Eliminato");
    load();
  };

  return (
    <div data-testid="client-registry-section" className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Anagrafica</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">Clienti</h1>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="client-pdf-input"
            onChange={(e) => handleImportPdf(e.target.files?.[0])}
          />
          <input
            ref={csvRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="client-csv-input"
            onChange={(e) => handleImportCsv(e.target.files?.[0])}
          />
          <input
            ref={xmlRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            multiple
            className="hidden"
            data-testid="client-xml-input"
            onChange={(e) => handleImportXml(Array.from(e.target.files || []))}
          />
          <Button
            data-testid="import-fatturapa-xml-button"
            variant="outline"
            onClick={() => xmlRef.current?.click()}
            disabled={importing}
          >
            <FileCode className="w-4 h-4 mr-2" />
            Import Aruba XML
          </Button>
          <Button
            data-testid="clients-template-button"
            variant="outline"
            onClick={() => window.open(clientsTemplateCsvUrl(), "_blank")}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Modello CSV
          </Button>
          <Button
            data-testid="import-clients-csv-button"
            variant="outline"
            onClick={() => csvRef.current?.click()}
            disabled={importing}
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importa CSV
          </Button>
          <Button
            data-testid="import-client-pdf-button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
          >
            <Upload className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button
            data-testid="export-invoicex-csv-button"
            variant="outline"
            onClick={() => window.open(exportClientsCsvUrl(), "_blank")}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Invoicex CSV
          </Button>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-button" className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuovo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Codice Cliente</Label>
                  <Input data-testid="client-code-input" value={form.codice_cliente} onChange={(e) => setForm({ ...form, codice_cliente: e.target.value })} placeholder="C001" />
                </div>
                <div className="col-span-2">
                  <Label>Ragione sociale *</Label>
                  <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-2">Sede legale (per FatturaPA · non geocodificata)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Indirizzo sede legale</Label>
                    <Input data-testid="client-sede-legale-address-input" value={form.sede_legale_address} onChange={(e) => setForm({ ...form, sede_legale_address: e.target.value })} placeholder="Via Roma 12" />
                  </div>
                  <div>
                    <Label>Città sede legale</Label>
                    <Input data-testid="client-sede-legale-city-input" value={form.sede_legale_city} onChange={(e) => setForm({ ...form, sede_legale_city: e.target.value })} placeholder="Milano" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label>CAP sede legale</Label>
                    <Input data-testid="client-sede-legale-cap-input" value={form.sede_legale_cap} onChange={(e) => setForm({ ...form, sede_legale_cap: e.target.value })} placeholder="20121" />
                  </div>
                  <div>
                    <Label>Provincia sede legale</Label>
                    <Input data-testid="client-sede-legale-prov-input" maxLength={2} value={form.sede_legale_provincia} onChange={(e) => setForm({ ...form, sede_legale_provincia: e.target.value.toUpperCase() })} placeholder="MI" />
                  </div>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Sede operativa (per mappa · geocodificata)</div>
                  <Button
                    type="button"
                    data-testid="copy-sede-legale-button"
                    variant="outline"
                    size="sm"
                    onClick={copyFromSedeLegale}
                    disabled={!form.sede_legale_address}
                    className="h-7 text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" /> Da indirizzo sede legale
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Indirizzo</Label>
                    <Input data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Facoltativo — se vuoto il cliente non compare in mappa" />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input data-testid="client-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Coordinate GPS (opzionale — bypass geocoding automatico)
                  </Label>
                  <Input
                    data-testid="client-coords-input"
                    value={form.coords_text || ""}
                    onChange={(e) => setForm({ ...form, coords_text: e.target.value })}
                    placeholder="es. 45.4642, 9.1900   (copia da Google Maps → click destro sul punto)"
                    className="font-mono text-xs"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Se compilato, ha priorità sull'indirizzo per il posizionamento in mappa. Utile quando Nominatim non trova l'indirizzo esatto.
                  </div>
                </div>
              </div>
              <div>
                <Label>Referente</Label>
                <Input data-testid="client-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefono</Label>
                  <Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-2">Dati fiscali (per FatturaPA / Invoicex)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>P.IVA</Label>
                    <Input data-testid="client-piva-input" value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} />
                  </div>
                  <div>
                    <Label>Codice Fiscale</Label>
                    <Input data-testid="client-cf-input" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div>
                    <Label>CAP</Label>
                    <Input data-testid="client-cap-input" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input data-testid="client-prov-input" maxLength={2} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} placeholder="MI" />
                  </div>
                  <div>
                    <Label>Cod. Destinatario</Label>
                    <Input data-testid="client-coddest-input" maxLength={7} value={form.codice_destinatario} onChange={(e) => setForm({ ...form, codice_destinatario: e.target.value.toUpperCase() })} placeholder="0000000" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>PEC</Label>
                  <Input data-testid="client-pec-input" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="client-save-button" onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white">
                {editId ? "Salva" : "Crea"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <ArrowUpDown className="w-3.5 h-3.5" />
        <span className="font-medium">Ordina per:</span>
        <Select value={sortKey} onValueChange={setSortKey}>
          <SelectTrigger data-testid="clients-sort-key" className="h-8 bg-white w-[190px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="name">Ragione sociale</SelectItem>
            <SelectItem value="codice_cliente">Codice cliente</SelectItem>
            <SelectItem value="city">Città</SelectItem>
            <SelectItem value="provincia">Provincia</SelectItem>
            <SelectItem value="contact_name">Referente</SelectItem>
            <SelectItem value="piva">P.IVA</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortDir} onValueChange={setSortDir}>
          <SelectTrigger data-testid="clients-sort-dir" className="h-8 bg-white w-[140px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="asc">A → Z / 0 → 9</SelectItem>
            <SelectItem value="desc">Z → A / 9 → 0</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-slate-400 ml-2">· {sortedItems.length} clienti</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedItems.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12">Nessun cliente. Aggiungine uno per iniziare.</div>
        )}
        {sortedItems.map((c) => (
          <Card key={c.id} data-testid={`client-card-${c.id}`} className="p-5 card-hover border-slate-200 bg-white">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold font-display text-slate-900 truncate">{c.name}</h3>
                <div className="mt-1.5 text-xs text-slate-600 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {c.address}{c.city ? `, ${c.city}` : ""}
                </div>
                {c.contact_name && (
                  <div className="mt-1 text-xs text-slate-600">{c.contact_name}</div>
                )}
                {c.phone && (
                  <div className="mt-1 text-xs text-slate-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</div>
                )}
                {c.email && (
                  <div className="mt-1 text-xs text-slate-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</div>
                )}
              </div>
              <div className="flex gap-1">
                <Button data-testid={`edit-client-${c.id}`} variant="ghost" size="sm" onClick={() => edit(c)}>
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button data-testid={`delete-client-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c.id)}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </div>
            {c.lat && (
              <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-500 font-mono">
                {c.lat.toFixed(4)}, {c.lng.toFixed(4)}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
